/**
 * Formats de réponse de l'API fournisseur (`/supplier/v1`), repris des DTO du back
 * (`back/src/modules/api/v1/**\/dto/*-response.dto.ts`), et libellés français de l'onglet de test.
 */

export type SupplierApiStatus = 'new' | 'confirmed' | 'finalized' | 'rejected';

export type SupplierApiLineType = 'product' | 'service' | 'interim' | 'catering';

export type AvailabilityDelayUnit = 'days' | 'hours' | 'minutes';

export interface SupplierApiPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Résultat exploitable d'un appel : données typées ou message d'erreur de l'API. */
export interface SupplierApiCall<T> {
  ok: boolean;
  status: number;
  data: T | null;
  pagination: SupplierApiPagination | null;
  errorMessage: string | null;
}

export interface SupplierApiListQuery {
  page: number;
  limit: number;
  status?: string;
}

export interface SupplierApiIdentity {
  tenant: { _id: string; label: string; technicalIdentifier: string };
  supplier: { _id: string; label: string; technicalIdentifier: string };
}

export interface SupplierApiAmountQuantity {
  value: number;
  unit?: string | null;
}

export interface SupplierApiContact {
  contact?: string;
  contactDetails?: string;
}

export interface SupplierApiBillingAddress {
  address?: string;
  addressComplement?: string;
  postalCode?: string;
  city?: string;
}

// ─── Expressions de besoin ─────────────────────────────────────────

export interface SupplierApiTicket {
  _id: string;
  technicalIdentifier?: string;
  establishment?: { _id: string; technicalIdentifier?: string; label: string; siret?: string; clientNumber?: string };
  lineCount: number;
  totalAmountHT: number;
  apiStatus: SupplierApiStatus;
  createdAt: string;
}

export interface SupplierApiTicketLine {
  _id: string;
  lineId: string;
  referenceId?: string;
  ticketId: string;
  supplierReference?: string;
  lineType: SupplierApiLineType;
  quantity: SupplierApiAmountQuantity;
  priceExcludingTax: number;
  availabilityDelay?: { value: number; unit: AvailabilityDelayUnit };
  interimData?: {
    availabilityPeriod?: { startDate?: string; endDate?: string };
    justification?: string;
    selectedProfile?: string;
  };
  cateringData?: { packaging?: string };
  apiStatus: SupplierApiStatus;
  createdAt: string;
}

// ─── Commandes ─────────────────────────────────────────────────────

export interface SupplierApiOrder {
  _id: string;
  technicalIdentifier?: string;
  ticketId?: string;
  orderDate: string;
  establishment?: { establishmentId: string; label: string; siret?: string; customerNumber?: string };
  numberOfLines: number;
  totalAmountExcludingTax: number;
  vatAmount: number;
  totalAmountIncludingTax: number;
  purchaseOrderContact?: SupplierApiContact;
  authorizationDate?: string;
  deliveryAddress?: { line1?: string; line2?: string; postalCode?: string; city?: string };
  billingAddress?: SupplierApiBillingAddress;
  apiStatus: SupplierApiStatus;
  createdAt: string;
}

export interface SupplierApiOrderLine {
  _id: string;
  lineId?: string;
  referenceId?: string;
  label?: string;
  supplierReference?: string;
  lineType: SupplierApiLineType;
  contract?: { _id: string; reference?: string };
  unit?: string;
  quantity: SupplierApiAmountQuantity;
  priceExcludingTax: number;
  vatAmount?: number;
  totalAmountIncludingTax?: number;
  theoreticalAvailabilityDate?: string;
  deliveryInformation: { deliveredQuantity: number; receivedQuantity: number; remainingQuantity: number };
  createdAt: string;
}

// ─── Facturables ───────────────────────────────────────────────────

export interface SupplierApiBillable {
  _id: string;
  orderId: string;
  reception: { _id: string; technicalIdentifier?: string; receptionDate?: string; receptionStatus?: string };
  establishment?: { _id: string; technicalIdentifier?: string; label: string; siret?: string; customerNumber?: string };
  numberOfLines: number;
  totalAmountExcludingTax: number;
  vatAmount: number;
  totalAmountIncludingTax: number;
  purchaseOrderContact?: SupplierApiContact;
  billingAddress?: SupplierApiBillingAddress;
  billingContact?: SupplierApiContact;
  paymentDeadline?: string;
}

export interface SupplierApiBillableLine {
  _id: string;
  lineId: string;
  referenceId?: string;
  label?: string;
  supplierReference?: string;
  quantity: { value?: number; unit?: string };
  priceExcludingTax: number;
  vatAmount?: number;
  totalAmountIncludingTax: number;
  createdAt: string;
}

// ─── Libellés ──────────────────────────────────────────────────────

export const TICKET_STATUS_LABELS: Record<SupplierApiStatus, string> = {
  new: 'Nouveau',
  confirmed: 'Confirmé',
  finalized: 'Finalisé',
  rejected: 'Refusé',
};

export const ORDER_STATUS_LABELS: Record<SupplierApiStatus, string> = {
  new: 'Nouvelle',
  confirmed: 'Confirmée',
  finalized: 'Finalisée',
  rejected: 'Refusée',
};

export const LINE_TYPE_LABELS: Record<SupplierApiLineType, string> = {
  product: 'Produit',
  service: 'Prestation',
  interim: 'Intérim / compétence',
  catering: 'Restauration',
};

export const DELAY_UNIT_LABELS: Record<AvailabilityDelayUnit, string> = {
  days: 'jours',
  hours: 'heures',
  minutes: 'minutes',
};

// ─── Présentation ──────────────────────────────────────────────────

export const API_STATUS_CLASSES: Record<SupplierApiStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-amber-100 text-amber-800',
  finalized: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export const HTTP_METHOD_CLASSES: Record<string, string> = {
  GET: 'bg-blue-100 text-blue-800',
  POST: 'bg-green-100 text-green-800',
  PATCH: 'bg-amber-100 text-amber-800',
};

export function httpStatusClass(status: number): string {
  if (status >= 200 && status < 300) return 'bg-green-100 text-green-800';
  if (status >= 400 && status < 500) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

/** JSON indenté pour l'affichage, ou texte brut tel quel. */
export function formatSupplierApiBody(body: unknown): string {
  if (body == null) return '';
  return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
}

export function formatSupplierApiAddress(address: {
  line1?: string;
  line2?: string;
  address?: string;
  addressComplement?: string;
  postalCode?: string;
  city?: string;
}): string {
  if (!address) return '';
  const street = [address.line1 ?? address.address, address.line2 ?? address.addressComplement].filter(Boolean);
  const town = [address.postalCode, address.city].filter(Boolean).join(' ');
  return [...street, town].filter(Boolean).join(', ');
}

export function formatSupplierApiContact(contact: SupplierApiContact): string {
  if (!contact) return '';
  return [contact.contact, contact.contactDetails].filter(Boolean).join(' · ');
}
